import { Box, Button, Stack, Text } from '@vouched.id/vault';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ApprovedIcon } from '../../Icons/Approved';
import { Tooltip } from '../Tooltip';
import { useTableStyles } from '../utils';
import { ReviewIcon } from './ReviewIcon';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { updateReview } from 'store/sagas/sagas';
const normalizeOverride = (value) => (typeof value === 'boolean' ? value : null);
// Reviewers only override the fields they disagree with — untouched fields
// keep their Vouched result (only a green ApprovedIcon counts as approved,
// mirroring ReviewIcon), and the backend derives review_success purely from
// the overrides (computeReviewSuccess). Submit is enabled once the review
// differs from the persisted one (jobReview) AND reaches a terminal decision
// (VOU-6832):
//   • reject  — at least one field is overridden to rejected;
//   • approve — every field is approved (by Vouched or by override), with at
//     least one override so a rejected field is never silently approved;
//   • clear   — every override is removed; submitting resets the job to its
//     Vouched result (review_success → null, no webhook).
export const computeCanSubmitReview = (dropdownItems, reviewItems, jobReview) => {
    let hasOverride = false;
    let hasRejection = false;
    let allApproved = true;
    let differsFromReview = false;
    for (const item of dropdownItems) {
        const label = item.reviewLabel;
        const override = normalizeOverride(reviewItems[label]);
        const isOverride = override !== null;
        const vouchedApproved = item.statusIcon.type === ApprovedIcon;
        const effectiveApproved = isOverride ? override : vouchedApproved;
        if (isOverride) {
            hasOverride = true;
        }
        if (override === false) {
            hasRejection = true;
        }
        if (!effectiveApproved) {
            allApproved = false;
        }
        if (override !== normalizeOverride(jobReview?.[label])) {
            differsFromReview = true;
        }
    }
    // Nothing changed since the last persisted review — nothing to submit.
    if (!differsFromReview) {
        return false;
    }
    return hasRejection || (allApproved && hasOverride) || !hasOverride;
};
export const ReviewTable = ({ dropdownItems, jobReview, jobSid, onReviewSubmitted }) => {
    const { asyncDispatch, dispatch } = useGlobalState();
    const isAdminRoute = useLocation().pathname.includes('/admin');
    const [reviewItems, setReviewItems] = useState(jobReview ?? {});
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Per-field clicks update local state only; nothing is persisted (and no
    // review webhook fires) until the reviewer submits (VOU-6832).
    const handleReviewUpdate = useCallback((item) => {
        setReviewItems((prev) => ({ ...prev, ...item }));
    }, []);
    const reviewLabels = useMemo(() => dropdownItems.map((item) => item.reviewLabel), [dropdownItems]);
    const canSubmitReview = useMemo(() => computeCanSubmitReview(dropdownItems, reviewItems, jobReview), [dropdownItems, jobReview, reviewItems]);
    const handleSubmitReview = useCallback(() => {
        setIsSubmitting(true);
        asyncDispatch(updateReview(jobSid, reviewItems))
            .then((job) => {
            // Sync local state and the parent's persisted-review baseline (the
            // jobReview prop) to the server-confirmed review, so follow-up
            // submissions — changing or undoing this one — gate against the
            // persisted state without needing a page refresh.
            setReviewItems(job.review ?? {});
            onReviewSubmitted(job);
            dispatch(setMessages({ value: 'Review submitted', severity: 'success' }));
        })
            .catch((error) => {
            console.error('Error submitting review:', error);
            dispatch(setMessages({ value: 'Unable to submit review', severity: 'error' }));
        })
            .finally(() => {
            setIsSubmitting(false);
        });
    }, [asyncDispatch, dispatch, jobSid, reviewItems, onReviewSubmitted]);
    const { tableStyles, thStyles, tdStyles } = useTableStyles();
    return (React.createElement(Stack, { spacing: "spacing.default" },
        React.createElement("table", { style: tableStyles },
            React.createElement("tbody", null,
                React.createElement("tr", null,
                    React.createElement("th", { style: thStyles }, "Field"),
                    React.createElement("th", { style: thStyles }, "Vouched"),
                    React.createElement("th", { style: thStyles }, "Review")),
                dropdownItems.map((item) => (React.createElement("tr", { key: item.text },
                    React.createElement("td", { style: tdStyles },
                        React.createElement(Box, { alignItems: "center", display: "flex", justifyContent: "flex-start", style: { gap: '5px' } },
                            React.createElement(Text, null, item.text),
                            item.tooltip && (React.createElement(Box, null,
                                React.createElement(Tooltip, null, item.tooltip))))),
                    React.createElement("td", { style: tdStyles },
                        React.createElement(Box, { alignItems: "center", display: "flex" }, item.statusIcon)),
                    React.createElement("td", { style: tdStyles },
                        React.createElement(Box, { alignItems: "center", display: "flex" },
                            React.createElement(ReviewIcon, { handleReviewUpdate: handleReviewUpdate, label: item.reviewLabel, originalResultIcon: item.statusIcon, reviewItems: reviewItems })))))))),
        !isAdminRoute && reviewLabels.length > 0 && (React.createElement(Box, { alignItems: "center", display: "flex", justifyContent: "flex-start", style: { gap: '10px' } },
            React.createElement(Button, { disabled: !canSubmitReview || isSubmitting, onClick: handleSubmitReview, title: isSubmitting ? 'loading' : '' }, "Submit Review"),
            !canSubmitReview && (React.createElement(Text, null, "Approve every rejected field to pass the job, reject a field, or clear your overrides to reset the job, before submitting."))))));
};
